import React from 'react';
import { Menu } from 'antd';
import {
  buildRoute,
  isRouterPage,
  Link,
  Routable,
  RouterPage,
  RouterPageGroup,
  useLocation,
} from '../Router';

export interface IRoutableMenuProps {
  pages: Routable[];
  collapsed?: boolean;
  mode?: React.ComponentProps<typeof Menu>['mode'];
  theme?: 'light' | 'dark';
  style?: React.CSSProperties;
  onSelect?: (page: RouterPage) => void;
}

export const RoutableMenu: React.FC<IRoutableMenuProps> = ({
  mode = 'inline',
  pages,
  theme = 'dark',
  collapsed = false,
  onSelect,
  ...props
}) => {
  const { pathname } = useLocation();
  const pathParts = pathname.split('/').filter((p) => p !== '');

  function renderMenuItem(
    page: RouterPage,
    collapsed: boolean,
    parentRoute?: string
  ) {
    return {
      key: page.key || null,
      title: page.menuName,
      icon: page.icon,
      label: (
        <>
          {!collapsed && <span>{page.menuName}</span>}
          <Link to={buildRoute(page.route, parentRoute)} />
        </>
      ),
      onClick: () => onSelect && onSelect(page),
    };
  }

  function renderSubMenu(
    group: RouterPageGroup,
    collapsed: boolean,
    parentRoute?: string
  ) {
    return {
      key: group.route?.replace('/', ''),
      label: !collapsed ? group.menuName : '',
      icon: group.icon,
      children: group.pages
        .filter((p) => !!p.menuName)
        .map((page) =>
          renderRoutableMenu(page, false, buildRoute(parentRoute, group.route))
        ),
    };
  }

  function renderRoutableMenu(
    routable: Routable,
    collapsed: boolean,
    parentRoute?: string
  ) {
    return isRouterPage(routable)
      ? renderMenuItem(routable, collapsed, parentRoute)
      : renderSubMenu(routable, collapsed, parentRoute);
  }

  const menuItems = pages
    .filter((p) => !!p.menuName)
    .map((p) => renderRoutableMenu(p, collapsed));

  return (
    <Menu
      defaultSelectedKeys={[pathname]}
      defaultOpenKeys={pathParts}
      mode={mode}
      theme={theme}
      style={props.style}
      items={menuItems}
    />
  );
};
